/**
 * KnowledgeTopBar — sticky white masthead shared by every Knowledge Center
 * page. Brand + a "Documentation" hover/focus dropdown (live collection
 * counts) + FAQs/Release notes/Support links on the left, a search
 * and "Back to app" button on the right. Collapses nav + search at
 * `media.tablet` (matches the mockup's own 680px cutoff).
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  // Aliased: the bare name would shadow the DOM's global KeyboardEvent,
  // which the document-level Escape listener below is typed against.
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BookOpen, ChevronDown, Search } from 'lucide-react';
import { media } from '../../../../styles/media';
import { ROUTES, helpArticlePath, helpCollectionPath } from '../../../../routes/paths';
import {
  collectionsWithCounts,
  getCollection,
  searchHelp,
} from '../../SupportWidget/help/helpContent';
import { useSupportWidgetStore } from '../../../../stores/supportWidgetStore';
import { RELEASE_NOTES_PUBLISHED } from '../releaseNotesVisibility';

const Bar = styled.header`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.zIndex.sticky};
`;

const Wrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing[8]};
  display: flex;
  align-items: center;
  gap: 26px;
  height: 56px;

  ${media.tablet} {
    padding: 0 ${({ theme }) => theme.spacing[4]};
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  white-space: nowrap;
`;

const Logo = styled.img`
  height: 26px;
  width: auto;
  display: block;
`;

const Divider = styled.span`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.colors.borders.light};
`;

/* A Link (not a bare span) so the wordmark doubles as "Home" for the
   Knowledge Center's own reading chrome, which renders without the app
   Header/Sidebar and would otherwise leave a reader with no top-level way
   back to /help besides the browser's own Back button. */
const Wordmark = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.body};
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.text.body};
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.sky};
    outline-offset: 2px;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};

  ${media.tablet} {
    display: none;
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 18px ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.body};

  &:hover {
    color: ${({ theme }) => theme.colors.primary.base};
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.sky};
    outline-offset: -2px;
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 18px ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.body};
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    color: ${({ theme }) => theme.colors.primary.base};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.sky};
    outline-offset: -2px;
  }
`;

/* Shown either by JS state ($open, driven by click/Enter/Space + Escape +
   outside-click) or by the CSS-only :hover/:focus-within fallback below —
   the fallback rule's higher specificity wins whenever it applies, so
   pointer users and keyboard users tabbing in without clicking still see it
   open, exactly as in the mockup. */
const Menu = styled.div<{ $open?: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing[2]};
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  width: 290px;
`;

const NavItem = styled.div`
  position: relative;

  &:hover ${Menu}, &:focus-within ${Menu} {
    display: block;
  }
`;

const MenuLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: 9px ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13.5px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.body};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.light};
    color: ${({ theme }) => theme.colors.primary.base};
    text-decoration: none;
  }
`;

const MenuRowSoon = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: 9px ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13.5px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.light};
`;

const CollectionCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.muted};
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  padding: 1px ${({ theme }) => theme.spacing[2]};
  min-width: 24px;
  text-align: center;

  ${MenuLink}:hover & {
    color: ${({ theme }) => theme.colors.primary.base};
    border-color: ${({ theme }) => theme.colors.primary.mist};
  }
`;

const MenuAll = styled(Link)`
  display: block;
  margin-top: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  font-size: 12.5px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary.base};

  &:hover {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.primary.dark};
  }
`;

const Right = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
`;

/* Search stays visible at every width. The nav links collapse below 768,
   and if search went with them an article page would have no way to reach
   another article except the breadcrumb — the hero search lives only on the
   index. So here it flexes to fill the room the nav gave up. */
const SearchForm = styled.form`
  position: relative;
  display: flex;
  align-items: center;

  ${media.tablet} {
    flex: 1;
    min-width: 0;
  }
`;

const SearchGlyph = styled(Search)`
  position: absolute;
  left: 11px;
  width: 15px;
  height: 15px;
  color: ${({ theme }) => theme.colors.text.muted};
  pointer-events: none;
`;

const SearchInput = styled.input`
  height: 34px;
  width: 216px;
  border: 1px solid ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 0 ${({ theme }) => theme.spacing[3]} 0 34px;
  font-size: 13px;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  transition:
    width 0.18s ease,
    border-color 0.15s,
    box-shadow 0.15s;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:focus {
    outline: none;
    width: 280px;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }

  ${media.tablet} {
    width: 100%;

    /* The focus expansion would overflow the 56px bar row once the field
       is already filling the available width. */
    &:focus {
      width: 100%;
    }
  }

  &:focus ~ ${SearchGlyph} {
    color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const BackToApp = styled(Link)`
  display: inline-flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.primary.base};
  color: ${({ theme }) => theme.colors.text.white};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 9px ${({ theme }) => theme.spacing[4]};
  font-size: 13.5px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  line-height: 1;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.primary.dark};
    text-decoration: none;
    color: ${({ theme }) => theme.colors.text.white};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.sky};
    outline-offset: 2px;
  }
`;

/* Typeahead results, anchored under the search field. Mirrors the
   Documentation menu's surface so the two dropdowns read as one system. */
const Suggestions = styled.ul`
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.spacing[2]});
  right: 0;
  width: 340px;
  max-height: 60vh;
  overflow-y: auto;
  margin: 0;
  padding: ${({ theme }) => theme.spacing[2]};
  list-style: none;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: 0 12px 34px rgba(17, 24, 39, 0.13);
  z-index: ${({ theme }) => theme.zIndex.dropdown};

  ${media.tablet} {
    width: 100%;
  }
`;

const SuggestionItem = styled.li`
  display: block;
`;

/* `$active` follows keyboard focus, which stays in the input the whole time
   (combobox pattern), so the highlight cannot rely on :focus. */
const SuggestionLink = styled(Link)<{ $active: boolean }>`
  display: block;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.backgrounds.light : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.light};
    text-decoration: none;
  }
`;

const SuggestionTitle = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const SuggestionMeta = styled.span`
  display: block;
  margin-top: 2px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const SuggestionEmpty = styled.li`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const SuggestionAll = styled(SuggestionLink)`
  margin-top: ${({ theme }) => theme.spacing[1]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary.base};
`;

/** Most a reader can scan without the bar becoming a page of its own. */
const MAX_SUGGESTIONS = 6;

export function KnowledgeTopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const openWidget = useSupportWidgetStore((s) => s.openWidget);
  const [query, setQuery] = useState('');
  // Static per session (derived from module-level content, not user state) —
  // memoized so it isn't rescanned on every search-box keystroke.
  const collections = useMemo(() => collectionsWithCounts(), []);

  /* ── Search typeahead ───────────────────────────────────────────────────
     Results appear as you type; the input keeps focus throughout and the
     highlight moves via aria-activedescendant (the combobox pattern), so a
     screen reader announces the option without the caret ever leaving the
     field. Enter opens the highlighted article, or runs a full search when
     nothing is highlighted. */
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef<HTMLFormElement>(null);

  const trimmedQuery = query.trim();
  const suggestions = useMemo(
    () => (trimmedQuery ? searchHelp(trimmedQuery).slice(0, MAX_SUGGESTIONS) : []),
    [trimmedQuery],
  );
  const showSuggestions = suggestionsOpen && trimmedQuery.length > 0;
  const allResultsPath = `${ROUTES.HELP}?q=${encodeURIComponent(trimmedQuery)}`;

  const closeSuggestions = () => {
    setSuggestionsOpen(false);
    setActiveIndex(-1);
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    if (!trimmedQuery) return;
    const picked = suggestions[activeIndex];
    closeSuggestions();
    navigate(picked ? helpArticlePath(picked.id) : allResultsPath);
  };

  const handleSearchKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      // First Escape dismisses the list; the browser's own handling of a
      // search input clearing on Escape stays available once it is closed.
      if (showSuggestions) {
        e.preventDefault();
        closeSuggestions();
      }
      return;
    }
    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSuggestionsOpen(true);
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSuggestionsOpen(true);
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    }
  };

  // Dismiss on outside pointerdown, matching the Documentation menu.
  useEffect(() => {
    closeSuggestions();
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!showSuggestions) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!searchRef.current?.contains(event.target as Node)) closeSuggestions();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [showSuggestions]);

  // Documentation dropdown — click/Enter/Space toggles it open (the button's
  // native activation already covers Enter/Space); :hover/:focus-within
  // (styled into Menu/NavItem below) keep it working for pointer users and
  // plain tabbing without a click. This state only adds Escape-to-close,
  // click-outside-to-close, and a real aria-expanded value.
  const [docsOpen, setDocsOpen] = useState(false);
  const docsNavItemRef = useRef<HTMLDivElement>(null);
  const docsTriggerRef = useRef<HTMLButtonElement>(null);

  // Closing on navigation covers the common case (a menu link was
  // activated); the links below also close explicitly on click as a
  // belt-and-braces fix for the edge case of activating a link that
  // doesn't actually change the route (e.g. "Browse all articles" while
  // already on /help).
  useEffect(() => {
    setDocsOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!docsOpen) return;

    const handlePointerDown = (e: PointerEvent) => {
      // Ignore clicks on the trigger/menu themselves — the trigger's own
      // onClick handles the toggle, and a menu link needs to actually
      // activate before anything closes it out from under the click.
      if (docsNavItemRef.current && !docsNavItemRef.current.contains(e.target as Node)) {
        setDocsOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDocsOpen(false);
        docsTriggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [docsOpen]);

  return (
    <Bar>
      <Wrap>
        <Brand>
          <Logo src="/assets/logo-small.png" alt="ABA Insights" />
          <Divider aria-hidden="true" />
          <Wordmark to={ROUTES.HELP}>
            <BookOpen aria-hidden="true" />
            Knowledge Center
          </Wordmark>
        </Brand>

        <Nav aria-label="Knowledge Center">
          <NavItem ref={docsNavItemRef}>
            <NavButton
              ref={docsTriggerRef}
              type="button"
              aria-haspopup="true"
              aria-expanded={docsOpen}
              onClick={() => setDocsOpen((o) => !o)}
            >
              Documentation
              <ChevronDown aria-hidden="true" size={11} />
            </NavButton>
            <Menu $open={docsOpen}>
              {collections.map(({ collection, count }) =>
                count > 0 ? (
                  <MenuLink
                    key={collection.id}
                    to={helpCollectionPath(collection.id)}
                    onClick={() => setDocsOpen(false)}
                  >
                    <span>{collection.title}</span>
                    <CollectionCount>{count}</CollectionCount>
                  </MenuLink>
                ) : (
                  <MenuRowSoon key={collection.id}>
                    <span>{collection.title}</span>
                    <CollectionCount>Soon</CollectionCount>
                  </MenuRowSoon>
                ),
              )}
              <MenuAll to={ROUTES.HELP} onClick={() => setDocsOpen(false)}>
                Browse all articles →
              </MenuAll>
            </Menu>
          </NavItem>
          <NavLink to={`${ROUTES.HELP}#faq`}>FAQs</NavLink>
          {RELEASE_NOTES_PUBLISHED && (
            <NavLink to={ROUTES.HELP_RELEASE_NOTES}>Release notes</NavLink>
          )}
          <NavButton type="button" onClick={() => openWidget()}>
            Support
          </NavButton>
        </Nav>

        <Right>
          <SearchForm role="search" onSubmit={handleSearch} ref={searchRef}>
            <SearchInput
              type="search"
              placeholder="Search"
              aria-label="Search the Knowledge Center"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSuggestionsOpen(true);
                setActiveIndex(-1);
              }}
              onFocus={() => setSuggestionsOpen(true)}
              onKeyDown={handleSearchKeyDown}
              role="combobox"
              aria-expanded={showSuggestions}
              aria-controls="kc-search-suggestions"
              aria-autocomplete="list"
              aria-activedescendant={
                activeIndex >= 0 ? `kc-search-option-${activeIndex}` : undefined
              }
              autoComplete="off"
            />
            <SearchGlyph aria-hidden="true" />

            {showSuggestions && (
              <Suggestions id="kc-search-suggestions" role="listbox" aria-label="Search results">
                {suggestions.length === 0 ? (
                  <SuggestionEmpty>No articles matched &ldquo;{trimmedQuery}&rdquo;.</SuggestionEmpty>
                ) : (
                  <>
                    {suggestions.map((article, i) => (
                      <SuggestionItem key={article.id} role="presentation">
                        <SuggestionLink
                          id={`kc-search-option-${i}`}
                          role="option"
                          aria-selected={i === activeIndex}
                          $active={i === activeIndex}
                          to={helpArticlePath(article.id)}
                          onClick={closeSuggestions}
                          onMouseEnter={() => setActiveIndex(i)}
                        >
                          <SuggestionTitle>{article.title}</SuggestionTitle>
                          <SuggestionMeta>
                            {getCollection(article.collectionId)?.title}
                          </SuggestionMeta>
                        </SuggestionLink>
                      </SuggestionItem>
                    ))}
                    <SuggestionItem role="presentation">
                      <SuggestionAll $active={false} to={allResultsPath} onClick={closeSuggestions}>
                        See all results for &ldquo;{trimmedQuery}&rdquo; →
                      </SuggestionAll>
                    </SuggestionItem>
                  </>
                )}
              </Suggestions>
            )}
          </SearchForm>
          <BackToApp to={ROUTES.DASHBOARD}>Back to app</BackToApp>
        </Right>
      </Wrap>
    </Bar>
  );
}

export default KnowledgeTopBar;
